import { getSessionStorage } from '@shared/core/storage';
import { addAction } from '@shared/utils/logger';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { withWindow } from '@shared/utils/withWindow';

export type ImageLoadFailureComponent = 'PhotoCarousel' | 'HighResPreload' | 'LazySharp' | 'LazyScreen' | 'HotelPreview';

type ImageHost = 'joy-media' | 'booking' | 'other';

type ReportImageLoadFailureArgs = Readonly<{
  component: ImageLoadFailureComponent;
  src: string;
  context?: string;
}>;

type ParsedImageUrl = Readonly<{
  url: string;
  hostname: string;
  proxiedHostname?: string;
  host: ImageHost;
  rendition?: string;
}>;

const IMAGE_LOAD_FAILED_ACTION = 'ImageLoadFailed';
const REPORTED_IMAGE_FAILURES_STORAGE_KEY = 'joy.imageLoadFailed.reported';
const MAX_STORED_IMAGE_FAILURES = 200;

// Third-party hotel photos are proxied as withjoy.com/hotel-media/<upstream host>/<path>.
const HOTEL_MEDIA_PROXY_PREFIX = '/hotel-media/';
const RENDITION_PARAMS: ReadonlyArray<string> = ['rendition', 'width', 'resize', 'quality'];

let reportedKeys: Set<string> | undefined;

const isJoyHostname = (hostname: string) => hostname === 'withjoy.com' || hostname.endsWith('.withjoy.com');

const isBookingHostname = (hostname: string) => /(^|\.)(bstatic|booking)\.com$/.test(hostname);

const parseImageUrl = (src: string, baseUrl: string): ParsedImageUrl => {
  const trimmedSrc = src.trim();
  // new URL('', base) resolves to the page itself, which would misreport the page as the image.
  if (!trimmedSrc) return { url: '', hostname: '', host: 'other' };

  let parsed: URL;
  try {
    parsed = new URL(trimmedSrc, baseUrl);
  } catch {
    return { url: '', hostname: '', host: 'other' };
  }

  // data: and blob: URLs carry the payload in the URL itself, so only the scheme is reported.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { url: parsed.protocol, hostname: '', host: 'other' };
  }

  const { hostname, pathname, searchParams } = parsed;
  const isHotelMediaProxy = isJoyHostname(hostname) && pathname.startsWith(HOTEL_MEDIA_PROXY_PREFIX);
  const proxiedPath = isHotelMediaProxy ? pathname.slice(HOTEL_MEDIA_PROXY_PREFIX.length) : '';
  const proxiedHostEnd = proxiedPath.indexOf('/');
  const proxiedHostname = proxiedHostEnd > 0 ? proxiedPath.slice(0, proxiedHostEnd) : undefined;
  const host: ImageHost = isBookingHostname(proxiedHostname ?? hostname) ? 'booking' : isJoyHostname(hostname) && !isHotelMediaProxy ? 'joy-media' : 'other';
  const rendition = RENDITION_PARAMS.filter(param => searchParams.has(param))
    .map(param => `${param}=${searchParams.get(param)}`)
    .join('&');

  return {
    url: `${parsed.origin}${pathname}`,
    hostname,
    ...(proxiedHostname ? { proxiedHostname } : {}),
    host,
    ...(rendition ? { rendition } : {}),
  };
};

const loadReportedKeys = (): Set<string> => {
  try {
    const stored = getSessionStorage().getItem(REPORTED_IMAGE_FAILURES_STORAGE_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []);
  } catch {
    return new Set();
  }
};

const persistReportedKeys = (keys: Set<string>) => {
  try {
    getSessionStorage().setItem(REPORTED_IMAGE_FAILURES_STORAGE_KEY, JSON.stringify(Array.from(keys).slice(-MAX_STORED_IMAGE_FAILURES)));
  } catch {
    // Quota exceeded or storage blocked: the in-memory set still dedupes for this page load.
  }
};

/**
 * Reports a failed image load as one `ImageLoadFailed` New Relic PageAction, at most once per
 * component + URL (query ignored) per browser-tab session. Image failures are expected and every
 * caller already falls back, so they are actions rather than JS errors. Bots and SSR are skipped.
 */
export function reportImageLoadFailure({ component, src, context }: ReportImageLoadFailureArgs): void {
  const pageUrl = withWindow<string | undefined>(win => win.location.href, undefined);
  if (pageUrl === undefined || isBotClient()) return;

  const image = parseImageUrl(src, pageUrl);
  const key = `${component}|${image.url}`;

  reportedKeys ??= loadReportedKeys();
  if (reportedKeys.has(key)) return;
  reportedKeys.add(key);
  persistReportedKeys(reportedKeys);

  addAction(IMAGE_LOAD_FAILED_ACTION, {
    component,
    ...image,
    ...(context ? { context } : {}),
  });
}
